import { WorkwearExpressBundleDataOptionSelectionQtyInterface } from 'swagger/ts/Definitions';
import { selectVariantFromOptions } from 'src/components/ProductDetail/utils/selectVariantsFromOptions';
import { BundleConfig } from 'src/components/BundleProduct/types';
import camelcaseKeys from 'camelcase-keys';
import { HAVE_MY_LOGO } from 'src/constants/artwork';

import { Customisation, FormData, Bundle } from '../types';

const getCustomisationData = ({ selectedOptions, selectedMethod, selectedArtwork }: Customisation, index: number) => {
    const defaultData = {
        customisationName: '',
        type: parseInt(selectedMethod!),
        specialInstructions: selectedOptions?.specialInstructions || '',
        isFree: index === 0,
        isArtworkMissing: selectedArtwork === 'logoExist',
        image: '',
        isContactRequested: false,
        textLineOne: '',
        textLineTwo: '',
        textLineThree: '',
        color: '',
        font: '',
    };
    if ('textLineOne' in selectedOptions! && 'image' in selectedOptions) {
        return {
            ...defaultData,
            id: selectedOptions.id,
            artworkId: selectedOptions.artworkId,
            isContactRequested: selectedOptions.isContactRequested,
            textLineOne: selectedOptions.textLineOne,
            textLineTwo: selectedOptions.textLineTwo || defaultData.textLineTwo,
            textLineThree: selectedOptions.textLineThree || defaultData.textLineThree,
            color: selectedOptions.color,
            font: selectedOptions.font,
            image: selectedOptions.image,
            customisationName: selectedOptions.customisationName,
            type: selectedOptions.type,
        };
    } else if ('textLineOne' in selectedOptions!) {
        return {
            ...defaultData,
            textLineOne: selectedOptions.textLineOne,
            textLineTwo: selectedOptions.textLineTwo || defaultData.textLineTwo,
            textLineThree: selectedOptions.textLineThree || defaultData.textLineThree,
            color: selectedOptions.color,
            font: selectedOptions.font,
        };
    } else if ('image' in selectedOptions!) {
        return {
            ...defaultData,
            image: selectedOptions.image || defaultData.image,
        };
    } else if ('isContactRequested' in selectedOptions!) {
        return {
            ...defaultData,
            isContactRequested: ['contact', HAVE_MY_LOGO].includes(selectedOptions.isContactRequested) ? true : false,
            is_artwork_missing: selectedOptions.isContactRequested === HAVE_MY_LOGO,
        };
    } else {
        return defaultData;
    }
};

export const convertFormDataToPayload = (data: FormData, sku: string, bundles: BundleConfig[]) => {
    const bundleCustomisation = data.customisations.map((customisation, index) => {
        if (customisation.selectedArtworkExisting) {
            return {
                location: customisation?.selectedPositions?.[0] || '',
                customisation: {
                    ...camelcaseKeys(JSON.parse(customisation.selectedArtworkExisting), { deep: true }),
                    isFree: index === 0,
                },
            };
        } else {
            return {
                location: customisation?.selectedPositions?.[0] || '',
                customisation: getCustomisationData(customisation, index),
            };
        }
    });

    const optionSelections = (bundle: Bundle, index: number) => {
        const optionSelections: number[] = [];
        const optionSelectionsQty: WorkwearExpressBundleDataOptionSelectionQtyInterface[] = [];
        const selectedBundle = bundles[index];

        if (!('sizes' in selectedBundle))
            return {
                optionSelections,
                optionSelectionsQty,
            };

        bundle.selectedColorsAndSizes.forEach((colorAndSize) => {
            colorAndSize.sizes.forEach((size) => {
                const selections = { color: colorAndSize.colorId, size: size.id };
                const variant = selectVariantFromOptions(selections, selectedBundle.variants!);
                if (variant) {
                    const simpleSku = variant.sku;
                    const match = selectedBundle.bundleOptions?.items[simpleSku];
                    if (match) {
                        optionSelections.push(match.optionItemID);
                        optionSelectionsQty.push({
                            selection_id: match.optionItemID,
                            selection_qty: size.quantity,
                        });
                    } else {
                        console.error('no match found for sku `%s`', variant.sku);
                    }
                }
            });
        });

        return {
            optionSelections,
            optionSelectionsQty,
        };
    };

    const bundleOptionsData = data.bundles.map((bundle, index) => {
        const selectedBundle = bundles[index];

        const bundleData = (optionId: number) => ({
            option_id: optionId,
            option_qty: 1,
            option_selections: optionSelections(bundle, index).optionSelections,
            extension_attributes: {
                option_selections_qty: optionSelections(bundle, index).optionSelectionsQty,
            },
        });

        if (!('sizes' in selectedBundle)) return bundleData(0);

        return bundleData(selectedBundle.bundleOptions?.optionId);
    });

    return {
        quote_id: '-1',
        sku,
        qty: 1,
        product_option: {
            extension_attributes: {
                bundled_customisation: bundleCustomisation,
                bundle_options: bundleOptionsData,
            },
        },
    };
};
